import { showUserAlert } from "../utils/userErrorMessage";
// src/pages/DriverHolidaysNew.jsx
import React, { useEffect, useMemo, useState } from "react";
import Header from "../Component/Header";
import { supabase } from "../supabase/client";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";

const pad2 = (n) => (n < 10 ? `0${n}` : `${n}`);
const toYmd = (y, m, d) => `${y}-${pad2(m)}-${pad2(d)}`;

const REASONS = ["휴무", "사고", "병가", "경조", "기타"];

export default function DriverHolidaysNew() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]); // 화면용 상태 (기사 목록 + 입력값)
  const [saving, setSaving] = useState(false);

  // 콤보 기본값: 오늘
  const [today] = useState(() => new Date());
  const thisYear = today.getFullYear();
  const years = useMemo(() => Array.from({ length: 7 }, (_, i) => thisYear - 3 + i), [thisYear]);
  const months = useMemo(() => Array.from({ length: 12 }, (_, i) => i + 1), []);
  const days = useMemo(() => Array.from({ length: 31 }, (_, i) => i + 1), []);

  // 기사 목록 로드
  useEffect(() => {
    (async () => {
      const company = localStorage.getItem("email");
      if (!company) return;

      setLoading(true);
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("id, driver_name, car_number, company")
          .eq("role", "driver")
          .eq("company", company)
          .order("driver_name", { ascending: true });

        if (error) throw error;

        // 화면 입력용 필드 추가 (체크박스/사유/기간)
        const init = (data || []).map((p) => ({
          id: p.id,
          driver_name: p.driver_name || "",
          car_number: p.car_number || "",
          company: p.company,
          checked: false,
          reason: "휴무",
          fromY: thisYear,
          fromM: today.getMonth() + 1,
          fromD: today.getDate(),
          toY: thisYear,
          toM: today.getMonth() + 1,
          toD: today.getDate(),
        }));

        setRows(init);
      } catch (e) {
        console.error("profiles 불러오기 실패:", e.message);
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [thisYear, today]);

  const updateRow = (idx, patch) => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  const handleSave = async () => {
    if (saving) return;
    const company = localStorage.getItem("email");
    if (!company) return;
    const company_name = localStorage.getItem("companyName") || company;

    // 선택된 행만 추출
    const selected = rows.filter((r) => r.checked);
    if (selected.length === 0) {
      showUserAlert("등록할 기사를 선택하세요.");
      return;
    }

    // payload 구성
    const payload = selected.map((r) => ({
      company,
      company_name,
      driver: r.driver_name,
      car_number: r.car_number || null,
      name: r.reason, // 구분
      from: toYmd(r.fromY, r.fromM, r.fromD),
      to: toYmd(r.toY, r.toM, r.toD),
    }));

    setSaving(true);
    try {
      const { error } = await supabase.from("holidays_driver").insert(payload);
      if (error) throw error;
      showUserAlert("등록이 완료되었습니다.");
      navigate(-1);
    } catch (e) {
      showUserAlert("등록 실패: " + (e?.message || "알 수 없는 오류"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <Header />
      <div className="p-4 space-y-4">
        <h2 className="text-lg font-semibold">기사 휴일 등록</h2>

        <Card>
          <CardContent className="bg-white">
            {loading ? (
              <div className="p-4">불러오는 중...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="table-auto w-full border text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border p-2">선택</th>
                      <th className="border p-2">기사명</th>
                      <th className="border p-2">구분</th>
                      <th className="border p-2">차량번호</th>
                      <th className="border p-2">휴무일 (시작)</th>
                      <th className="border p-2">휴무일 (끝)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="border p-4 text-center text-gray-500">
                          등록 가능한 기사가 없습니다.
                        </td>
                      </tr>
                    ) : (
                      rows.map((r, idx) => (
                        <tr key={r.id || idx}>
                          {/* 선택 */}
                          <td className="border p-2 text-center">
                            <input
                              type="checkbox"
                              checked={r.checked}
                              onChange={(e) => updateRow(idx, { checked: e.target.checked })}
                            />
                          </td>

                          {/* 기사명 */}
                          <td className="border p-2">{r.driver_name || "-"}</td>

                          {/* 구분(사유) 콤보 */}
                          <td className="border p-2">
                            <select
                              className="border rounded px-2 py-1"
                              value={r.reason}
                              onChange={(e) => updateRow(idx, { reason: e.target.value })}
                            >
                              {REASONS.map((s) => (
                                <option key={s} value={s}>
                                  {s}
                                </option>
                              ))}
                            </select>
                          </td>

                          {/* 차량번호 */}
                          <td className="border p-2">{r.car_number || ""}</td>

                          {/* 휴무 시작 */}
                          <td className="border p-2">
                            <div className="flex items-center gap-1">
                              <select
                                className="border rounded px-2 py-1"
                                value={r.fromY}
                                onChange={(e) => updateRow(idx, { fromY: Number(e.target.value) })}
                              >
                                {years.map((y) => (
                                  <option key={y} value={y}>
                                    {y}
                                  </option>
                                ))}
                              </select>
                              <select
                                className="border rounded px-2 py-1"
                                value={r.fromM}
                                onChange={(e) => updateRow(idx, { fromM: Number(e.target.value) })}
                              >
                                {months.map((m) => (
                                  <option key={m} value={m}>
                                    {m}
                                  </option>
                                ))}
                              </select>
                              <select
                                className="border rounded px-2 py-1"
                                value={r.fromD}
                                onChange={(e) => updateRow(idx, { fromD: Number(e.target.value) })}
                              >
                                {days.map((d) => (
                                  <option key={d} value={d}>
                                    {d}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </td>

                          {/* 휴무 끝 */}
                          <td className="border p-2">
                            <div className="flex items-center gap-1">
                              <select
                                className="border rounded px-2 py-1"
                                value={r.toY}
                                onChange={(e) => updateRow(idx, { toY: Number(e.target.value) })}
                              >
                                {years.map((y) => (
                                  <option key={y} value={y}>
                                    {y}
                                  </option>
                                ))}
                              </select>
                              <select
                                className="border rounded px-2 py-1"
                                value={r.toM}
                                onChange={(e) => updateRow(idx, { toM: Number(e.target.value) })}
                              >
                                {months.map((m) => (
                                  <option key={m} value={m}>
                                    {m}
                                  </option>
                                ))}
                              </select>
                              <select
                                className="border rounded px-2 py-1"
                                value={r.toD}
                                onChange={(e) => updateRow(idx, { toD: Number(e.target.value) })}
                              >
                                {days.map((d) => (
                                  <option key={d} value={d}>
                                    {d}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 하단 버튼 */}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => navigate(-1)} disabled={saving}>
            돌아가기
          </Button>
          <Button onClick={handleSave} disabled={saving || loading}>
            {saving ? "등록중..." : "등록"}
          </Button>
        </div>
      </div>
    </div>
  );
}
